import { useEffect, useState } from 'react';
import first from 'lodash.first';
import { loadCountries } from 'redux/reducers/countries';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'redux/rootReducer';
import { IRadioSpinsItem, ICountry, IValues } from './../types';

interface ICountryPattern {
  displayName: string;
  flagImgLarge: string;
  flagImgMedium: string;
  flagImgSmall: string;
  id: string;
  lat: number;
  lon: number;
}

interface ICountryMetadata {
  countryId: string;
  values: IValues;
}

interface ICountryExtended extends ICountryPattern {
  values: IValues;
  countryId: string;
}

/*
 * This hook creates and returns
 * - an array of territory rows with rank, country info and values based on the active metric
 * - a d3.js scale callback based on the value range of the rows
 *
 * @param {Array} radioSpinsData the data retrieved from the /api/radio/spins endpoint
 * @param {String} activeMetric which metric values to gather.
 * @returns {Array} normalized territory rows and scale callback
 */
const useRadioSpinsData: any = (radioSpinsData: IRadioSpinsItem[]) => {
  const [territoryRows, setTerritoryRows] = useState();

  const dispatch = useDispatch();

  useEffect(() => {
    // Load country metadata on mount
    dispatch(loadCountries());
  }, [dispatch]);

  const countries = useSelector((state: RootState) => state.countries.data);

  useEffect(() => {
    if (!radioSpinsData || !countries || !countries.length) return;
    const rows = first<any>(radioSpinsData).countries.map(mapToRowData);
    const rowsWithCountryData = rows.map(addCountryMetaData(countries));

    setTerritoryRows(rowsWithCountryData);
  }, [countries, radioSpinsData]);

  return territoryRows;
};

const mapToRowData = (
  countryInfo: ICountry
): { countryId: string; values: IValues } => ({
  countryId: countryInfo.countryId,
  values: countryInfo.data,
});

function addCountryMetaData(countries: ICountryPattern[]): Function {
  return function (
    row: ICountryMetadata
  ): ICountryExtended | { countryId: string; values: IValues } {
    const isSameId = (country: ICountryPattern): boolean =>
      country.id === row.countryId;
    return { ...row, ...countries.find(isSameId) };
  };
}

export default useRadioSpinsData;
